/**
 * Cart drawer: stop a stale re-render from wiping the line items.
 *
 * Impact's <cart-drawer>._onCartChanged delays rewriting .cart-drawer__inner by 1250ms for any
 * change that is not "variant:add" (so the line-item removal animation can finish), and nothing
 * cancels that timer. Remove a line and add something within ~1.25s and the older snapshot lands
 * last: the drawer shows the pre-add cart (sometimes no line items at all) while the count and
 * footer come from the newer one. Reproduced 2026-09-20: server cart 2 items, drawer 1 row.
 *
 * This patch keeps the theme's behaviour but (1) cancels a pending rewrite whenever a newer cart
 * change arrives, and (2) falls back to a full section fetch when a cart update carries no drawer
 * section (an app updating the cart without asking for it would otherwise throw and leave the
 * drawer stale). Everything else is delegated to the original method.
 */
(() => {
  const sectionIdOf = (el) => {
    const section = el.classList.contains('shopify-section') ? el : el.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  const patch = (element) => {
    if (!element || element.prototype.__smileCartPatched) return;
    const original = element.prototype._onCartChanged;
    if (typeof original !== 'function') return;
    element.prototype.__smileCartPatched = true;

    element.prototype._onCartChanged = function (event) {
      clearTimeout(this.__smileCartTimer);

      const html = event?.detail?.cart?.sections?.[sectionIdOf(this)];
      if (!html) {
        this._onCartRefresh?.();
        return;
      }

      const updated = new DOMParser().parseFromString(html, 'text/html');
      const updatedInner = updated.querySelector('.cart-drawer__inner');
      const currentInner = this.querySelector('.cart-drawer__inner');
      if (!(event.detail.cart.item_count > 0) || !currentInner || !updatedInner) {
        // Emptying the cart is the theme's own path: it awaits a fade before swapping in the empty
        // state, and that fade never finishes while the tab is in the background. Reconcile after.
        const result = original.call(this, event);
        this.__smileReconcile();
        return result;
      }

      const footer = this.querySelector('[slot="footer"]');
      const updatedFooter = updated.querySelector('[slot="footer"]');
      if (footer && updatedFooter) footer.replaceChildren(...updatedFooter.childNodes);

      this.__smileCartTimer = setTimeout(() => {
        this.__smileCartTimer = null;
        currentInner.innerHTML = updatedInner.innerHTML;
        this.__smileReconcile();
      }, event.detail.baseEvent === 'variant:add' ? 0 : 1250);
    };

    /**
     * Safety net: whatever the race (a stale section payload, a detached node, an app touching the
     * cart), the drawer must not show a different cart than the server has. Compare the rendered
     * lines with /cart.js shortly after a write and re-fetch the whole section when they disagree.
     */
    element.prototype.__smileReconcile = function () {
      clearTimeout(this.__smileCheckTimer);
      this.__smileCheckTimer = setTimeout(async () => {
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
          const rendered = this.querySelectorAll('.cart-drawer__line-items > *').length;
          if (rendered !== cart.items.length) this._onCartRefresh?.();
        } catch (e) {
          /* offline or cart unavailable: leave the drawer as it is */
        }
      }, 700);
    };
  };

  if (window.customElements) {
    customElements.whenDefined('cart-drawer').then(() => patch(customElements.get('cart-drawer')));
  }

  // The drawer opens on variant:add. Reconcile then too: if that add's cart:change never reached
  // the drawer (seen when the buy section re-renders around the click), the drawer would otherwise
  // sit on the previous cart until the next page load.
  document.addEventListener('variant:add', () => {
    document.querySelector('cart-drawer')?.__smileReconcile?.();
  });
})();
